import { useMemo, useRef } from 'react'
import { imageUrl } from 'app/frontend/base/helpers'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import ActorAbilityContainer from './ActorAbilityContainer'
import SpellIcon from './SpellIcon'
import { ActorTimelineRow } from './ActorTimelineContainer'
import useObservedWidth from '../hooks/useObservedWidth'
import useTimelineTicks from '../hooks/useTimelineTicks'
import useVisibleTimelineTicks from '../hooks/useVisibleTimelineTicks'
import { formatTimelineTimestamp } from './timelineMath'
import { NOTE_ICON_URL } from '../config/generalAbilities'

const ROW_HEIGHT = 20
const ROW_GAP = 0
const ICON_SIZE = 20
const ICON_INSET = 1
const TICK_LABEL_AREA_HEIGHT = 20

type ReadonlyCooldownTimelineProps = {
  rows: ActorTimelineRow[]
  maxTime: number
  spellNameById: Record<number, string>
  timelineMinWidthClass?: string
}

const ReadonlyCooldownTimeline = ({
  rows,
  maxTime,
  spellNameById,
  timelineMinWidthClass = 'min-w-0',
}: ReadonlyCooldownTimelineProps) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const timelineWidth = useObservedWidth(rootRef)
  const safeMaxTime = Math.max(maxTime, 1)
  const visibleRange = useMemo(
    () => ({ start: 0, end: safeMaxTime }),
    [safeMaxTime],
  )
  const { minorTickTimes, minuteTickTimes } = useTimelineTicks({
    maxTime: safeMaxTime,
    phaseDividers: [],
  })
  const { visibleMinorTickTimes, visibleMinuteTickTimes } =
    useVisibleTimelineTicks({
      minorTickTimes,
      minuteTickTimes,
      visibleRange,
    })
  const rowsHeight =
    rows.length * ROW_HEIGHT + Math.max(rows.length - 1, 0) * ROW_GAP
  const svgHeight = rowsHeight + TICK_LABEL_AREA_HEIGHT
  const minuteTickLabelTimes = useMemo(
    () => minuteTickTimes.filter((tickAt) => tickAt + 15 <= safeMaxTime),
    [minuteTickTimes, safeMaxTime],
  )
  const toX = (time: number) =>
    ((time - visibleRange.start) / safeMaxTime) * timelineWidth
  const toWidth = (duration: number) =>
    Math.max((Math.max(duration, 1) / safeMaxTime) * timelineWidth, 1)

  return (
    <div className='flex items-start gap-1'>
      <ActorAbilityContainer
        labels={
          <>
            {rows.map((row) => (
              <div
                key={row.key}
                className='flex items-center gap-2 rounded-sm transition-colors'
              >
                {row.spellId === null ? (
                  <div className='w-40 max-w-40 shrink-0 h-5 text-xs text-foreground truncate flex items-center gap-2'>
                    <span className='truncate grow text-right'>Message</span>
                    <img
                      src={NOTE_ICON_URL}
                      className='w-5 h-5 rounded border border-border'
                    />
                  </div>
                ) : (
                  <a
                    href={`https://wowhead.com/spell=${row.spellId}`}
                    target='_blank'
                    rel='noreferrer'
                    className='w-40 max-w-40 shrink-0 h-5 text-xs text-foreground truncate flex items-center gap-2'
                  >
                    <span className='truncate grow text-right'>
                      {spellNameById[row.spellId] ?? `Spell ${row.spellId}`}
                    </span>
                    <SpellIcon
                      spellId={row.spellId}
                      className='w-5 h-5 rounded border border-border'
                    />
                  </a>
                )}
              </div>
            ))}
          </>
        }
      />
      <div className={`grow ${timelineMinWidthClass}`} ref={rootRef}>
        <svg
          className='block w-full'
          width='100%'
          height={svgHeight}
          viewBox={`0 0 ${timelineWidth} ${svgHeight}`}
        >
          <g className='text-card'>
            <rect
              x={0}
              y={0}
              width={timelineWidth}
              height={rowsHeight}
              fill='currentColor'
              rx={4}
              ry={4}
            />
          </g>
          <g className='text-border'>
            <rect
              x={0}
              y={0}
              width={timelineWidth}
              height={rowsHeight}
              fill='transparent'
              stroke='currentColor'
              strokeWidth={1.25}
              vectorEffect='non-scaling-stroke'
              rx={8}
              ry={8}
            />
          </g>
          <g className='text-border'>
            {visibleMinorTickTimes.map((tickAt) => {
              const x = toX(tickAt)
              return (
                <line
                  key={`minor-tick-${tickAt}`}
                  x1={x}
                  x2={x}
                  y1={0}
                  y2={rowsHeight}
                  stroke='currentColor'
                  strokeWidth={1}
                  opacity={0.45}
                />
              )
            })}
          </g>
          <g className='text-foreground'>
            {visibleMinuteTickTimes.map((tickAt) => {
              const x = toX(tickAt)
              return (
                <line
                  key={`minute-tick-${tickAt}`}
                  x1={x}
                  x2={x}
                  y1={0}
                  y2={rowsHeight}
                  stroke='currentColor'
                  strokeWidth={1}
                  opacity={0.3}
                />
              )
            })}
          </g>
          {rows.map((row, rowIndex) => {
            const y = rowIndex * (ROW_HEIGHT + ROW_GAP)
            return (
              <g key={row.key}>
                <rect
                  x={0}
                  y={y}
                  width={timelineWidth}
                  height={ROW_HEIGHT}
                  fill='transparent'
                />
                {row.casts.map((cast) => {
                  const castStart = cast.at
                  const castDurationEnd = cast.at + Math.max(cast.duration, 0)
                  const castCooldownEnd = cast.at + Math.max(cast.cooldown, 0)
                  const isCastStartVisible =
                    castStart >= visibleRange.start &&
                    castStart <= visibleRange.end
                  const durationVisibleStart = Math.max(
                    castStart,
                    visibleRange.start,
                  )
                  const durationVisibleEnd = Math.min(
                    castDurationEnd,
                    visibleRange.end,
                  )
                  const cooldownVisibleStart = Math.max(
                    castDurationEnd,
                    visibleRange.start,
                  )
                  const cooldownVisibleEnd = Math.min(
                    castCooldownEnd,
                    visibleRange.end,
                  )
                  const durationVisibleWidth =
                    durationVisibleEnd > durationVisibleStart
                      ? toWidth(durationVisibleEnd - durationVisibleStart)
                      : 0
                  const cooldownVisibleWidth =
                    cooldownVisibleEnd > cooldownVisibleStart
                      ? toWidth(cooldownVisibleEnd - cooldownVisibleStart)
                      : 0
                  const abilityName =
                    cast.spellId === null
                      ? 'Message'
                      : spellNameById[cast.spellId] ?? `Spell ${cast.spellId}`
                  return (
                    <g key={cast.id}>
                      {durationVisibleWidth > 0 ? (
                        <rect
                          x={toX(durationVisibleStart)}
                          y={y + 1}
                          width={durationVisibleWidth}
                          height={ROW_HEIGHT - 2}
                          fill={cast.color}
                          fillOpacity={0.7}
                          pointerEvents='none'
                        />
                      ) : null}
                      {cooldownVisibleWidth > 0 ? (
                        <rect
                          x={toX(cooldownVisibleStart)}
                          y={y + 1}
                          width={cooldownVisibleWidth}
                          height={ROW_HEIGHT - 2}
                          fill={cast.color}
                          fillOpacity={0.3}
                          pointerEvents='none'
                        />
                      ) : null}
                      {isCastStartVisible ? (
                        <>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <image
                                x={toX(cast.at) + ICON_INSET}
                                y={y + ICON_INSET}
                                width={ICON_SIZE - ICON_INSET * 2}
                                height={ICON_SIZE - ICON_INSET * 2}
                                href={
                                  cast.spellId === null
                                    ? NOTE_ICON_URL
                                    : imageUrl(`icons/${cast.spellId}.jpg`)
                                }
                                preserveAspectRatio='xMidYMid slice'
                              />
                            </TooltipTrigger>
                            <TooltipContent side='top' className='text-center'>
                              <div>{abilityName}</div>
                              {cast.note?.trim().length ? (
                                <div className='text-xs text-muted-foreground text-center whitespace-pre-line'>
                                  {cast.note.trim()}
                                </div>
                              ) : null}
                              <div className='text-xs text-muted-foreground text-center'>
                                {formatTimelineTimestamp(cast.at)}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                          <g className='pointer-events-none'>
                            <text
                              x={toX(cast.at) + ICON_SIZE + 3}
                              y={y + 1 + ROW_HEIGHT / 2}
                              fill={'var(--color-accent-foreground)'}
                              fontSize={12}
                              dominantBaseline='middle'
                            >
                              {formatTimelineTimestamp(cast.at)}
                            </text>
                          </g>
                        </>
                      ) : null}
                    </g>
                  )
                })}
              </g>
            )
          })}
          <g className='text-muted-foreground'>
            {minuteTickLabelTimes.map((tickAt) => {
              const x = toX(tickAt)
              return (
                <text
                  key={`minute-tick-label-${tickAt}`}
                  x={x}
                  y={rowsHeight + 14}
                  fill='currentColor'
                  fontSize={12}
                  textAnchor='middle'
                >
                  {formatTimelineTimestamp(tickAt)}
                </text>
              )
            })}
          </g>
        </svg>
      </div>
    </div>
  )
}

export default ReadonlyCooldownTimeline
